// src/data/intelNotesClient.js
//
// LOCATION-INTEL-TAB-S1: Client for /api/locations/:id/intel-notes.
//
// Provenance/note-log layer only — never feeds or changes scoring.
// Pattern mirrors adminClient.js.

import {
  NetworkError,
  ValidationError,
  errorFromResponse,
} from "./errors.js";
import { log } from "../utils/logger.js";

// ─── HTTP helper ──────────────────────────────────────────────────────────────

async function apiFetch(url, options = {}) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (err) {
    throw new NetworkError(`Network error: ${err.message}`);
  }
  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  if (res.status === 204) return null;
  return res.json();
}

function assertLocationId(locationId, fnName) {
  if (!locationId || typeof locationId !== "string") {
    throw new ValidationError(`${fnName}: locationId required`);
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Fetch a location's intel notes, newest first. viewer+ (server enforces).
 *
 * @param {string} locationId
 * @returns {Promise<Array>}
 */
export async function getIntelNotes(locationId) {
  assertLocationId(locationId, "getIntelNotes");
  const list = await apiFetch(`/api/locations/${encodeURIComponent(locationId)}/intel-notes`);
  if (!Array.isArray(list)) {
    log.warn("intelNotesClient: unexpected non-array from GET intel-notes");
    return [];
  }
  return list;
}

/**
 * Fetch a location's soft-deleted intel notes for the restore view.
 * editor+ only (server enforces; 403 for viewers).
 *
 * @param {string} locationId
 * @returns {Promise<Array>}
 */
export async function getDeletedIntelNotes(locationId) {
  assertLocationId(locationId, "getDeletedIntelNotes");
  const list = await apiFetch(
    `/api/locations/${encodeURIComponent(locationId)}/intel-notes?includeDeleted=1`
  );
  if (!Array.isArray(list)) {
    log.warn("intelNotesClient: unexpected non-array from GET deleted intel-notes");
    return [];
  }
  return list;
}

/**
 * Add an intel note. editor+ only (server enforces).
 *
 * @param {string} locationId
 * @param {object} payload — { note, tag, sourceKind, sourceLabel?, linkedField?, attachmentUrl? }
 * @returns {Promise<object>} created note
 */
export async function addIntelNote(locationId, payload) {
  assertLocationId(locationId, "addIntelNote");
  if (!payload || typeof payload !== "object") {
    throw new ValidationError("addIntelNote: payload required");
  }
  const created = await apiFetch(`/api/locations/${encodeURIComponent(locationId)}/intel-notes`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(payload),
  });
  log.ui("intelNotesClient: created intel note", { id: created.id, locationId });
  return created;
}

/**
 * Update an intel note. editor+ only (server enforces).
 *
 * @param {string} locationId
 * @param {string} noteId
 * @param {object} patch
 * @returns {Promise<object>} updated note
 */
export async function updateIntelNote(locationId, noteId, patch) {
  assertLocationId(locationId, "updateIntelNote");
  if (!noteId || typeof noteId !== "string") {
    throw new ValidationError("updateIntelNote: noteId required");
  }
  if (!patch || typeof patch !== "object") {
    throw new ValidationError("updateIntelNote: patch required");
  }
  const updated = await apiFetch(
    `/api/locations/${encodeURIComponent(locationId)}/intel-notes/${encodeURIComponent(noteId)}`,
    {
      method:  "PATCH",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(patch),
    }
  );
  log.ui("intelNotesClient: updated intel note", { id: noteId, locationId });
  return updated;
}

/**
 * Restore a soft-deleted intel note. editor+ only (server enforces).
 *
 * @param {string} locationId
 * @param {string} noteId
 * @returns {Promise<object>} restored note
 */
export async function restoreIntelNote(locationId, noteId) {
  assertLocationId(locationId, "restoreIntelNote");
  if (!noteId || typeof noteId !== "string") {
    throw new ValidationError("restoreIntelNote: noteId required");
  }
  const restored = await apiFetch(
    `/api/locations/${encodeURIComponent(locationId)}/intel-notes/${encodeURIComponent(noteId)}/restore`,
    { method: "POST" }
  );
  log.ui("intelNotesClient: restored intel note", { id: noteId, locationId });
  return restored;
}

/**
 * Delete an intel note. editor+ only (server enforces).
 *
 * @param {string} locationId
 * @param {string} noteId
 * @returns {Promise<null>}
 */
export async function deleteIntelNote(locationId, noteId) {
  assertLocationId(locationId, "deleteIntelNote");
  if (!noteId || typeof noteId !== "string") {
    throw new ValidationError("deleteIntelNote: noteId required");
  }
  const result = await apiFetch(
    `/api/locations/${encodeURIComponent(locationId)}/intel-notes/${encodeURIComponent(noteId)}`,
    { method: "DELETE" }
  );
  log.ui("intelNotesClient: deleted intel note", { id: noteId, locationId });
  return result;
}
